<template>
  <div class="favorite">
    <div class="favorite-header">
      <div class="l-container">
        <h1 class="heading-l">Избранное</h1>
        <span v-if="modelsLength" class="tablet-count body-s-regular">
          {{ tabletCount }}
        </span>
      </div>
    </div>
    <div class="container l-container">
      <UiCurtain
        v-if="modelsLength > 0"
        :open="curtainVisible"
        @close="curtainVisible = false"
      >
        <div class="filters-wrap">
          <div class="title heading-m">Фильтры</div>
          <div class="description body-l-regular">
            {{ categoryStateLabel }}
          </div>
          <FiltersFavoriteFilter
            :filters="filters"
            :categories="favoriteCategories"
            :show-active="isMobile"
            :active-list="activeList"
            @update-categories="
              (data) => updateFilters(FavoriteFiltersTypes.CATEGORIES, data)
            "
            @update-availability="
              (data) => updateFilters(FavoriteFiltersTypes.AVAILABLE, data)
            "
            @cancel="cancelActiveFilter"
            @clear-all="cancelAllFilters"
          />
        </div>
      </UiCurtain>

      <div class="menu">
        <template v-if="!isEmptyState">
          <div class="options-bar">
            <ui-ui-link
              text="Очистить список"
              font-class="body-l-regular"
              icon-before="trash"
              @click="confirmDelete"
            >
              Очистить список
            </ui-ui-link>
            <div class="filters-button-wrap" @click="curtainVisible = true">
              <FiltersFiltersButton
                :count="activeList.length"
                :visible="curtainVisible"
              />
            </div>
          </div>
          <FiltersActiveFilters
            v-if="!isMobile"
            :list="activeList"
            @cancel="cancelActiveFilter"
            @clear-all="cancelAllFilters"
          />
          <div class="products-wrap">
            <div ref="productsList" class="products-list lp-m-b">
              <transition-group name="list" tag="div" mode="out-in">
                <ProductCardGlobalCardsMixer
                  v-for="(item, index) in visibleModels"
                  :key="`${item.id}_favorite_card`"
                  :model="item"
                  type-style="list"
                  type="model"
                  :offers="productsOffers[item.id]"
                  :lazy-img="!loadedImages[item.id]"
                  :discount-max="getDiscountMax(item)"
                  favorite-delete
                  class="product-card-favorite"
                  :position="index + 1"
                  block-sype="favorite"
                  @remove-favorite="removeFavorite"
                />
              </transition-group>
            </div>
          </div>
        </template>
        <template v-else>
          <UiEmptyState
            image="/static/images/heart.svg"
            title="В избранном пока ничего нет"
            class="lp-m-b"
          >
            Добавляйте товары в избранное и они появятся в данном разделе
          </UiEmptyState>
        </template>
        <model-history
          :no-padding="true"
          :is-fast="true"
          font-class="heading-m"
        />
      </div>
    </div>
    <ModalsConfirmModal
      v-if="showConfirmModal"
      title="Удаление списка"
      description="Вы хотите удалить все товары из избранного? Действие невозможно отменить."
      apply-text="Удалить список"
      cancel-text="Отменить"
      :on-apply="() => removeAllFavorite()"
      :on-close-action="() => $emit('close')"
      @close="showConfirmModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import numWord from '~/common/num-word.js';
import { API } from '~/api/api';
import { useListingsLazy } from '~/composition/useListingsLazy';
import type { ModelItem } from '~/interfaces/api';
import { useMediaQueryStore } from '~/stores/mediaQuery';
import { useFavoriteStore } from '~/stores/favorite';
import { useGlobalStore } from '~/stores/global';

import type {
  ActiveFilter,
  FavoriteCategories,
  FavoriteFilters,
} from '~/interfaces/app';
import { FavoriteFiltersTypes } from '~/interfaces/app';
import { base64ToObj, objToBase64 } from '~/utils/base64';

const ModelHistory = defineAsyncComponent(
  () => import('~/components/model-history/ModelHistory.vue')
);

interface FavoriteGroup {
  name: string;
  list: number[];
}

interface FavoriteGroups {
  [key: string]: FavoriteGroup;
}

const route = useRoute();
const router = useRouter();
const storeMedia = useMediaQueryStore();
const favoriteStore = useFavoriteStore();
const globalStore = useGlobalStore();
const isMobile = computed(
  () =>
    storeMedia.isMobile || storeMedia.isEs || storeMedia.isSm || storeMedia.isMd
);
const showConfirmModal = ref(false);
const curtainVisible = ref<boolean>(false);
const productsList = ref<HTMLElement>();
const { newObserve, productsOffers, loadedImages } = useListingsLazy(
  () => {},
  false,
  1,
  100,
  isMobile,
  productsList
);
const idsArr = computed((): number[] => favoriteStore.ids);
const models = ref<ModelItem[]>([]);
const modelsLength = computed(() => models.value?.length);
const filters = ref<FavoriteFilters>({
  categories:
    route.query.categories !== undefined
      ? String(route.query.categories)
          .split(',')
          .map((el) => +el)
      : [],
  available: route.query.available === null,
});
const isEmptyState = computed(() => !models.value.length);
const tabletCount = computed(() =>
  numWord(
    models.value.length,
    ['', 'товар', 'товара', 'товаров'],
    true,
    true,
    true
  )
);
// Здесь и в 5 местах ниже, де исп. pageRoute: добавить замыкающий слэш в урл. Убрать, если будет настройка в nuxt
const pageRoute: Ref = ref<string>(
  (route.path.endsWith('/') ? route.path.slice(0, -1) : route.path).replace(
    /\/favorite\/?$/,
    ''
  )
);

watch(
  () => idsArr.value,
  (newIds, oldIds) => {
    const needAdd = newIds.filter((i) => !oldIds.includes(i));
    const needDelete = oldIds.filter((i) => !newIds.includes(i));
    if (models.value.length) needDelete.forEach((id) => removeFavorite(id));
    if (needAdd.length) getModels(needAdd, false);
  }
);

watch(
  () => isMobile.value,
  (_, old) => {
    if (old) nextTick(() => newObserve('[data-id]'));
  }
);

const visibleModels = computed(() => {
  let tempModels = [...models.value];
  const categories = filters.value.categories.filter(
    (el) => favoriteGroups.value[el]
  );
  if (categories && categories.length)
    tempModels = applyCategoryFilter(tempModels, categories);

  if (filters.value.available)
    tempModels = applyAvailabilityFilter(tempModels, filters.value.available);

  nextTick(() => newObserve());
  return tempModels;
});

const favoriteGroups = computed<FavoriteGroups>(() =>
  models.value
    .map(({ id: modelId, category: { id, name } }) => ({
      id,
      modelId,
      name,
    }))
    .reduce((groups: FavoriteGroups, favorite) => {
      let list = [favorite.modelId];

      if (groups[favorite.id] && groups[favorite.id].list)
        list = [...groups[favorite.id].list, ...list];

      return {
        ...groups,
        [favorite.id]: {
          name: favorite.name,
          list,
        },
      };
    }, {})
);
const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
const favoriteCategories = computed<FavoriteCategories>(() => {
  return Object.entries(favoriteGroups.value).reduce(
    (categories, [categoryId, { name }]) => {
      return {
        ...categories,
        [categoryId]: capitalizeFirstLetter(name),
      };
    },
    {}
  );
});

const activeList = computed((): ActiveFilter[] => {
  const newActive: ActiveFilter[] = filters.value.categories
    .filter((el) => favoriteCategories.value[el])
    .map((el) => ({
      filter: 'category',
      name: favoriteCategories.value[el],
      id: el,
    }));

  if (filters.value.available)
    newActive.push({
      filter: 'available',
      name: 'В наличии',
      id: 0,
      item_id: 0,
    });

  return newActive;
});

const categoryStateLabel = computed(() => {
  const categoryNum = Object.keys(favoriteGroups.value).length;
  const productNum = Object.values(favoriteGroups.value).reduce(
    (sum, e) => (sum += e.list.length),
    0
  );

  return categoryNum
    ? `${numWord(
        productNum,
        ['', 'товар', 'товара', 'товаров'],
        true
      )} в ${numWord(
        categoryNum,
        ['', 'категории', 'категориях', 'категориях'],
        true
      )}`
    : '';
});

const getModels = async (ids: number[], init: boolean): Promise<any> => {
  try {
    const res = await API.get(`/models`, {
      params: { ids: ids.join(','), region_id: globalStore.region.id },
    });
    const newModels = res.data.filter((el: ModelItem) =>
      idsArr.value.includes(el.id)
    );
    if (!init) {
      models.value = [...models.value, ...newModels];
    }
    return newModels;
  } catch (err) {
    return null;
  }
};

const cancelActiveFilter = (item: ActiveFilter): void => {
  if (item.filter === 'available') {
    filters.value.available = false;
    if (router)
      router.replace({
        path: pageRoute.value + '/favorite/',
        query: { ...route.query, available: undefined },
      });
  }

  if (item.filter === 'category' && item.id) {
    filters.value.categories = filters.value.categories.filter(
      (cId) => cId !== item.id
    );
    if (router)
      router.replace({
        path: pageRoute.value + '/favorite/',
        query: {
          ...route.query,
          categories:
            filters.value.categories.length > 0
              ? filters.value.categories.join(',')
              : undefined,
        },
      });
  }
};

const cancelAllFilters = (): void => {
  if (filters.value.available || filters.value.categories.length > 0) {
    filters.value.available = false;
    filters.value.categories = [];
    if (router)
      router.replace({
        path: pageRoute.value + '/favorite/',
        query: {
          ...route.query,
          available: undefined,
          categories: undefined,
        },
      });
  }
};

const applyCategoryFilter = (models: ModelItem[], categories: number[]) => {
  return categories.length
    ? (categories
        .reduce(
          (ids: number[], categoryId) => [
            ...ids,
            ...(favoriteGroups.value?.[categoryId]?.list || []),
          ],
          []
        )
        .map((modelId) => models.find((m) => m.id === modelId)) as ModelItem[])
    : models;
};

const applyAvailabilityFilter = (models: ModelItem[], available: boolean) => {
  return available ? models.filter((m) => Number(m.offer_count) !== 0) : models;
};

const updateFilters = (type: string, { val, queryVal }: any) => {
  let categoriesVal = [];
  switch (type) {
    case FavoriteFiltersTypes.AVAILABLE: {
      if (router && route.query.available !== queryVal)
        router.replace({
          path: pageRoute.value + '/favorite/',
          query: { ...route.query, available: queryVal },
        });

      filters.value.available = val;
      break;
    }
    case FavoriteFiltersTypes.CATEGORIES:
      categoriesVal = val.filter((el: any) => favoriteCategories.value[el]);
      if (router && route.query.categories !== queryVal) {
        router.replace({
          path: pageRoute.value + '/favorite/',
          query: {
            ...route.query,
            categories: categoriesVal.join(',') || undefined,
          },
        });

        filters.value.categories = categoriesVal;
        break;
      }
  }
};

const removeFavorite = (id: number) => {
  if (visibleModels.value.length === 1 && visibleModels.value[0].id === id)
    cancelAllFilters();
  models.value = models.value.filter((i) => i.id !== id);
};

const removeAllFavorite = () => {
  if (models.value && models.value.length) {
    cancelAllFilters();
    models.value = [];
  }
  favoriteStore.clearFavorite();
};

const confirmDelete = () => {
  showConfirmModal.value = true;
};

const getDiscountMax = (product: ModelItem) => {
  return product?.discount_max || 0;
};

watch(
  () => curtainVisible.value,
  (value) => {
    if (value) {
      document.body.classList.add('tablet-overflow-hidden');
    } else {
      document.body.classList.remove('tablet-overflow-hidden');
    }
  }
);
const dataFavorite = await useAsyncData('dataFavorite', async () => {
  if (idsArr.value.length) {
    const data = await getModels(idsArr.value, true);
    return objToBase64({ data });
  } else {
    return null;
  }
});

if (dataFavorite.data.value) {
  models.value = base64ToObj(dataFavorite.data.value).data;
  if (!models.value?.length) {
    favoriteStore.setFavoriteState({ ids: [], count: {} });
  }
}
</script>
<style lang="scss" scoped>
.favorite {
  display: flex;
  flex-direction: column;

  .favorite-header {
    margin: 20px 0 0;

    h1 {
      display: inline;
      margin-right: 8px;
    }

    span {
      display: inline-block;
      @include lg-block {
        display: none;
      }
    }

    @include sm-block {
      margin-top: 24px;
    }
    @include lg-block {
      margin-top: 32px;
      margin-bottom: 32px;
    }
  }

  .tablet-count {
    color: $achromatic-disabled;
    @include lg-block {
      display: none;
    }
  }

  .container {
    display: flex;
    width: 100%;
    float: left;

    .filters-wrap {
      padding: 12px;
      width: 100%;

      @include md-block {
        padding: 20px;
      }

      @include lg-block {
        padding: 0 20px 0 0;
        width: 100%;
      }

      @include xl-block {
        padding-right: 12px;
      }

      .description {
        color: $achromatic-disabled;
        padding: 0 0 17px;
        border-bottom: 1px solid $achromatic-hover;
        display: none;

        @include lg-block {
          display: block;
        }
      }

      .title {
        display: block;

        @include lg-block {
          display: none;
        }
      }
    }

    .menu {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      background-color: $white;
      border-radius: 10px;
      width: 100%;

      @include lg-block {
        width: calc(100% - 240px);
      }

      @include xl-block {
        @include col-all(7);
      }

      .active-filters {
        padding-top: 24px;
        display: none;

        @include lg-block {
          display: flex;
        }
      }

      .options-bar {
        display: flex;
        height: 52px;
        justify-content: space-between;
        width: calc(100% + 24px);
        margin-left: -12px;
        padding: 8px 6px 4px 12px;
        border-bottom: 1px solid $achromatic-hover;
        @include sm-block {
          width: calc(100% + 48px);
          margin-left: -24px;
          padding-left: 24px;
          padding-right: 18px;
        }
        @include lg-block {
          width: 100%;
          margin-left: 0;
          height: 40px;
          padding: 0;
          padding-bottom: 16px;
        }
        @include xl-block {
          width: 100%;
          margin-left: 0;
        }

        .filters-button-wrap {
          display: flex;
          height: 40px;
          width: 40px;
          align-items: center;
          justify-content: center;
          @include md-block {
            width: auto;
          }
          @include lg-block {
            display: none;
          }
        }

        .text-button {
          &:deep(.icon) {
            display: none;

            @include lg-block {
              display: block;
            }
          }
        }
      }

      .products-wrap {
        padding-top: 16px;
        border: {
          radius: 0;
          left: none;
          right: none;
        }
        overflow: hidden;
        @include sm-block {
          padding-top: 20px;
        }

        @include lg-block {
          margin: 0;
          padding-top: 32px;
        }

        .products-list {
          display: grid;
          grid-template-columns: 100%;
          grid-auto-rows: auto;

          .p-card {
            min-width: unset;
            max-width: unset;
            max-height: unset;
            background-color: transparent;

            &:not(:last-child) {
              border-bottom: 1px solid $achromatic-hover;
            }
          }
        }
      }

      .model-history {
        padding-top: 48px;

        @include lg-block {
          padding-top: 64px;
        }
      }
    }
  }
}

.list-leave-active {
  transition: opacity 0.2s, min-height 0.3s, height 0.3s, padding 0.1s 0.2s;
}

.list-enter-active {
  transition: opacity 0.3s, min-height 0.3s, height 0.4s;
}

.list-enter, .list-leave-to /* .list-leave-active до версии 2.1.8 */ {
  opacity: 0;
  min-height: 0;
  height: 0;
  padding: 0;
}

:deep(.empty-state) {
  margin-top: 0;
}

.desktop-margin {
  margin-right: 8px;
}
</style>
